import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormRow } from '@gravity-ui/components';
import { Pencil, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Modal, Sheet, TextInput, Tooltip } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAdminUpdateUserMutation } from 'store/api';

import { CANCEL, EDIT_ACC, EMAIL, SAVE } from '../../constants';
import type { AccountEditForm } from '../utils/use-validation-edit';
import { useAccountEditValidation } from '../utils/use-validation-edit';

import styles from './styles.module.scss';

interface Person {
	id?: number;
	email: string;
	role: string;
	firstName: string;
	middleName: string;
	lastName: string;
}

interface AccountEditModalProps {
	account: Person;
	onAdd: () => void;
}

export const AccountEditModal = ({ account, onAdd }: AccountEditModalProps) => {
	const [open, setOpen] = useState(false);

	const [visible, setVisible] = useState(false);

	const [updateAccount] = useAdminUpdateUserMutation();

	const { accountEditSchema } = useAccountEditValidation();

	const methods = useForm<AccountEditForm>({
		resolver: zodResolver(accountEditSchema),
		defaultValues: {
			email: account?.email || '',
		},
	});

	const {
		handleSubmit,
		register,
		formState: { errors, isSubmitted },
	} = methods;

	const onSubmit = async (data: AccountEditForm) => {
		const userId = account.id;

		try {
			await updateAccount({ id: userId, body: data }).unwrap();
			onAdd();
			console.log('Данные успешно обновлены:', data);
			setOpen(false);
		} catch (error) {
			console.error('Ошибка при обновлении аккаунта:', error);
		}
	};

	return (
		<>
			<Button view="flat" size="l" onClick={() => setOpen(true)}>
				<Icon data={Pencil} size={16} />
			</Button>

			<Modal open={open} onClose={() => setOpen(false)} className={styles.modal}>
				<div className={styles.modal__title}>
					<h3>{EDIT_ACC}</h3>

					<Xmark onClick={() => setOpen(false)} cursor="pointer" />
				</div>
				<FormProvider {...methods}>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className={styles.modal__rows}>
							<FormRow className={styles.modal__row} label="ФИО">
								<TextInput
									defaultValue={`${account.firstName} ${account.middleName} ${account.lastName}`}
									view="clear"
								/>
							</FormRow>

							<FormRow className={styles.modal__row} required label={EMAIL}>
								<TextInput
									size="xl"
									hasClear
									{...register('email', { required: true })}
									validationState={isSubmitted && errors.email ? 'invalid' : undefined}
									errorPlacement="inside"
									errorMessage={
										isSubmitted && errors.email ? 'Обязательно для заполнения' : undefined
									}
								/>
							</FormRow>
						</div>

						<div className={styles.modal__btns}>
							<Button size="xl" view="flat-secondary" onClick={() => setOpen(false)}>
								{CANCEL}
							</Button>
							<Tooltip content="Внесите изменения" openDelay={100} placement="top">
								<div tabIndex={0}>
									<Button size="xl" type="submit" className={styles.modal__btns__sent}>
										{SAVE}
									</Button>
								</div>
							</Tooltip>
						</div>
					</form>
				</FormProvider>
			</Modal>
		</>
	);
};
